import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import { isPlatformApiRequest } from "@zenith/utils/journeySession";

/**
 * Paygilant step-up challenge (spec §10).
 *
 * When a checkpoint is enforced, the backend can refuse a transfer, refund or credential change with
 * 403 and one of the codes below, meaning "confirm it is you, then try again". The interceptor here
 * holds the failed request, asks the user to verify through RiskChallengeDialog, and sends the same
 * request again with the earned token in X-Risk-Challenge. Callers see only the final answer.
 */

export const RISK_CHALLENGE_HEADER = "X-Risk-Challenge";

/** Paygilant asked for a challenge, or the token from the last one was refused (spent or expired). */
const NEEDS_VERIFICATION = new Set([
  "ZENZ-TRANSACTION-069",
  "ZENZ-TRANSACTION-070",
  "ZENZ-USER-166",
  "ZENZ-USER-167",
]);

/** Paygilant refused the request outright; retrying cannot change it (see RiskBlockedModal). */
const BLOCKED = new Set(["ZENZ-TRANSACTION-057", "ZENZ-USER-154"]);

export function isRiskBlocked(
  internalCode: string | undefined | null
): boolean {
  return BLOCKED.has(internalCode ?? "");
}

/** How many times one request may ask the user to verify; the backend denies after a few on its own. */
export const MAX_CHALLENGES = 2;

type Listener = (pending: boolean) => void;

const listeners = new Set<Listener>();
let waiting: ((token: string | null) => void) | null = null;

function notify(pending: boolean): void {
  listeners.forEach((listener) => listener(pending));
}

/** For the dialog: called with true while a challenged request waits for the user. */
export function subscribeRiskChallenge(listener: Listener): () => void {
  listeners.add(listener);
  listener(waiting !== null);
  return () => {
    listeners.delete(listener);
  };
}

/** Asks the user to verify; resolves with the earned token, or null when they cancel. */
export function requestRiskChallenge(): Promise<string | null> {
  // One dialog at a time: an older request still waiting is answered as cancelled.
  waiting?.(null);
  return new Promise((resolve) => {
    waiting = resolve;
    notify(true);
  });
}

/** For the dialog: the user verified (with the token) or cancelled (null). */
export function completeRiskChallenge(token: string | null): void {
  const resolve = waiting;
  waiting = null;
  notify(false);
  resolve?.(token);
}

export function needsRiskChallenge(
  error: AxiosError<{ internalCode?: string }>
): boolean {
  return (
    error.response?.status === 403 &&
    NEEDS_VERIFICATION.has(error.response.data?.internalCode ?? "") &&
    isPlatformApiRequest(error.config?.url)
  );
}

type ChallengeableConfig = InternalAxiosRequestConfig & {
  _riskChallenges?: number;
};

/** Installs the interceptor on [instance] (the shared axios by default); returns its uninstaller. */
export function installRiskChallengeInterceptor(
  instance: AxiosInstance = axios
): () => void {
  const id = instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<{ internalCode?: string }>) => {
      const config = error.config as ChallengeableConfig | undefined;
      const challenges = config?._riskChallenges ?? 0;
      if (
        !config ||
        !needsRiskChallenge(error) ||
        challenges >= MAX_CHALLENGES
      ) {
        return Promise.reject(error);
      }
      const token = await requestRiskChallenge();
      if (!token) {
        return Promise.reject(error);
      }
      config._riskChallenges = challenges + 1;
      config.headers.set(RISK_CHALLENGE_HEADER, token);
      return instance(config);
    }
  );
  return () => instance.interceptors.response.eject(id);
}
